/**
 * NodeCloner - Handles cloning and creating new waypoints/nodes
 * 
 * Responsible for:
 * - Cloning entire parent nodes with updates
 * - Adding new waypoints by cloning existing ones
 */
export class NodeCloner {
  /**
   * Clone an entire parent node with all its child waypoints
   * @param {Document} xmlDoc - The XML document
   * @param {Element} parentNodeElement - The parent Contributed element to clone
   * @param {Map} waypointUpdates - Map of waypoint index to {position, rotation, viaPoint} updates
   * @returns {Array<number>} Array of indices of the newly created waypoints
   */
  cloneParentNode(xmlDoc, parentNodeElement, waypointUpdates) {
    // Clone the entire parent node structure
    const clonedParent = parentNodeElement.cloneNode(true);

    // Find all waypoints within the cloned parent
    const childPoses = clonedParent.querySelectorAll('Pose[key="way-point"]');

    // Apply updates to each child waypoint
    childPoses.forEach((poseElement, childIndex) => {
      this.applyWaypointUpdates(poseElement, childIndex, parentNodeElement, xmlDoc, waypointUpdates);
    });

    // Insert the cloned parent at the end
    const allPoses = xmlDoc.querySelectorAll('Pose[key="way-point"]');
    const lastPoseElement = allPoses[allPoses.length - 1];
    const lastParent = lastPoseElement.closest('Contributed');
    lastParent.parentNode.insertBefore(clonedParent, lastParent.nextSibling);

    // Return the indices of the newly created waypoints
    const newIndices = [];
    const newPoses = clonedParent.querySelectorAll('Pose[key="way-point"]');
    const startIndex = allPoses.length;
    for (let i = 0; i < newPoses.length; i++) {
      newIndices.push(startIndex + i);
    }
    return newIndices;
  }

  /**
   * Apply updates to a cloned waypoint
   * @private
   */
  applyWaypointUpdates(poseElement, childIndex, parentNodeElement, xmlDoc, waypointUpdates) {
    const parent = poseElement.closest('Contributed');
    const originalPoses = parentNodeElement.querySelectorAll('Pose[key="way-point"]');
    
    if (childIndex >= originalPoses.length) return;

    const originalPose = originalPoses[childIndex];
    const allPoses = Array.from(xmlDoc.querySelectorAll('Pose[key="way-point"]'));
    const globalIndex = allPoses.indexOf(originalPose);

    const update = waypointUpdates.get(globalIndex);
    if (!update) return;

    // Update position
    const positionElement = poseElement.querySelector('Position[key="posePosition"]');
    positionElement.querySelector('Length[key="X"]').setAttribute('value', update.position.x.toString());
    positionElement.querySelector('Length[key="Y"]').setAttribute('value', update.position.y.toString());
    positionElement.querySelector('Length[key="Z"]').setAttribute('value', update.position.z.toString());

    // Update rotation
    const rotationElement = poseElement.querySelector('Rotation[key="poseRotation"]');
    rotationElement.querySelector('Angle[key="RX"]').setAttribute('value', update.rotation.rx.toString());
    rotationElement.querySelector('Angle[key="RY"]').setAttribute('value', update.rotation.ry.toString());
    rotationElement.querySelector('Angle[key="RZ"]').setAttribute('value', update.rotation.rz.toString());

    // Update via-point if provided
    if (update.viaPoint) {
      const viaPointElement = parent.querySelector('Pose[key="via-point"]');
      if (viaPointElement) {
        this.updateViaPointFromUpdate(viaPointElement, update.viaPoint);
      }
    }
  }

  /**
   * Update a via-point element from update data
   * @private
   */
  updateViaPointFromUpdate(viaPointElement, viaPointUpdate) {
    const viaPosition = viaPointElement.querySelector('Position[key="posePosition"]');
    viaPosition.querySelector('Length[key="X"]').setAttribute('value', viaPointUpdate.position.x.toString());
    viaPosition.querySelector('Length[key="Y"]').setAttribute('value', viaPointUpdate.position.y.toString());
    viaPosition.querySelector('Length[key="Z"]').setAttribute('value', viaPointUpdate.position.z.toString());

    const viaRotation = viaPointElement.querySelector('Rotation[key="poseRotation"]');
    viaRotation.querySelector('Angle[key="RX"]').setAttribute('value', viaPointUpdate.rotation.rx.toString());
    viaRotation.querySelector('Angle[key="RY"]').setAttribute('value', viaPointUpdate.rotation.ry.toString());
    viaRotation.querySelector('Angle[key="RZ"]').setAttribute('value', viaPointUpdate.rotation.rz.toString());
  }

  /**
   * Add a new waypoint by cloning an existing one
   * @param {Document} xmlDoc - The XML document
   * @param {number} sourceIndex - Index of waypoint to clone
   * @param {Object} position - New position {x, y, z}
   * @param {Object} rotation - New rotation {rx, ry, rz}
   * @param {Object} options - Optional parameters
   * @param {Object} options.viaPoint - Optional via-point {position, rotation}
   * @param {Array<number>} options.jointPositions - Optional joint positions (6 values in radians)
   * @returns {number} Index of the newly created waypoint
   */
  addWaypoint(xmlDoc, sourceIndex, position, rotation, options = {}) {
    const poses = xmlDoc.querySelectorAll('Pose[key="way-point"]');
    if (sourceIndex < 0 || sourceIndex >= poses.length) {
      throw new Error(`Invalid source waypoint index: ${sourceIndex}`);
    }

    const sourcePoseElement = poses[sourceIndex];
    const sourceParent = sourcePoseElement.closest('Contributed');

    if (!sourceParent) {
      throw new Error(`Could not find parent element for waypoint ${sourceIndex}`);
    }

    // Clone the entire Contributed parent element
    const newContributed = sourceParent.cloneNode(true);

    // Update position and rotation
    this.updateClonedWaypoint(newContributed, position, rotation, options);

    // Insert at the end
    const allPoses = xmlDoc.querySelectorAll('Pose[key="way-point"]');
    const lastPoseElement = allPoses[allPoses.length - 1];
    const lastParent = lastPoseElement.closest('Contributed');
    lastParent.parentNode.insertBefore(newContributed, lastParent.nextSibling);

    return allPoses.length;
  }

  /**
   * Update a cloned waypoint with new values
   * @private
   */
  updateClonedWaypoint(newContributed, position, rotation, options) {
    const newPoseElement = newContributed.querySelector('Pose[key="way-point"]');
    
    // Update position
    const positionElement = newPoseElement.querySelector('Position[key="posePosition"]');
    positionElement.querySelector('Length[key="X"]').setAttribute('value', position.x.toString());
    positionElement.querySelector('Length[key="Y"]').setAttribute('value', position.y.toString());
    positionElement.querySelector('Length[key="Z"]').setAttribute('value', position.z.toString());

    // Update rotation
    const rotationElement = newPoseElement.querySelector('Rotation[key="poseRotation"]');
    rotationElement.querySelector('Angle[key="RX"]').setAttribute('value', rotation.rx.toString());
    rotationElement.querySelector('Angle[key="RY"]').setAttribute('value', rotation.ry.toString());
    rotationElement.querySelector('Angle[key="RZ"]').setAttribute('value', rotation.rz.toString());

    // Update via-point if provided
    if (options.viaPoint) {
      const viaPointElement = newContributed.querySelector('Pose[key="via-point"]');
      if (viaPointElement) {
        this.updateViaPointFromUpdate(viaPointElement, options.viaPoint);
      }
    }

    // Update joint positions if provided
    if (options.jointPositions && options.jointPositions.length === 6) {
      this.updateJointPositions(newContributed, options.jointPositions);
    }
  }

  /**
   * Update joint positions in a cloned waypoint
   * @private
   */
  updateJointPositions(newContributed, jointPositions) {
    const jointPositionsElement = newContributed.querySelector('JointPositions[key="way-point-joint"]');
    if (!jointPositionsElement) return;

    for (let i = 1; i <= 6; i++) {
      const jointElement = jointPositionsElement.querySelector(`JointPosition[key="[${i}]"]`);
      if (jointElement) {
        const angleElement = jointElement.querySelector('Angle[key="pos"]');
        if (angleElement) {
          angleElement.setAttribute('value', jointPositions[i - 1].toString());
        }
      }
    }
  }
}
